/**
 * Iter52 P13h — Widget "Vânzări incomplete".
 *
 * Afișează count-ul vânzărilor care nu sunt clasificate complet (lipsește
 * product_kind sau offer_id pe contract). Click pe card → navighează la
 * /sales?incomplete=true pentru completare manuală.
 *
 * Data: GET /api/dashboard/incomplete-sales
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { ClipboardList, AlertTriangle, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export const IncompleteSalesWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/dashboard/incomplete-sales`)
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData({ error: true }); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <div className="h-20 animate-pulse bg-slate-100 rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.error) return null;

  const {
    total_incomplete = 0,
    missing_product_kind = 0,
    missing_offer = 0,
    missing_both = 0,
    total_sales = 0,
    incomplete_percent = 0,
  } = data;

  const allComplete = total_incomplete === 0;
  const accentClass = allComplete
    ? "border-emerald-200 bg-emerald-50/50"
    : incomplete_percent > 10
    ? "border-amber-300 bg-amber-50/60"
    : "border-indigo-200 bg-indigo-50/50";
  const iconColor = allComplete
    ? "text-emerald-600 bg-emerald-100"
    : incomplete_percent > 10
    ? "text-amber-700 bg-amber-100"
    : "text-indigo-700 bg-indigo-100";

  return (
    <Card className={`${accentClass} shadow-sm`} data-testid="widget-incomplete-sales">
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`p-2.5 rounded-lg ${iconColor}`}>
            {allComplete ? <ClipboardList className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-semibold text-slate-900">📋 Vânzări incomplete</h3>
              <span
                className="text-[10px] uppercase tracking-wider text-slate-500"
                data-testid="widget-incomplete-percent"
              >
                {incomplete_percent}% din {total_sales}
              </span>
            </div>
            <p
              className="text-3xl font-bold text-slate-900 mt-1 tabular-nums"
              data-testid="widget-incomplete-count"
            >
              {total_incomplete}
            </p>
            {allComplete ? (
              <p className="text-[11px] text-emerald-700 mt-1">
                ✓ Toate vânzările au tipul de produs și ofertă setate.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[11px] text-slate-600">
                  {missing_product_kind > 0 && (
                    <span data-testid="widget-missing-product-kind">
                      <strong className="text-slate-900">{missing_product_kind}</strong> fără tip produs
                    </span>
                  )}
                  {missing_offer > 0 && (
                    <span data-testid="widget-missing-offer">
                      <strong className="text-slate-900">{missing_offer}</strong> fără ofertă
                    </span>
                  )}
                  {missing_both > 0 && (
                    <span className="text-rose-700" data-testid="widget-missing-both">
                      <strong>{missing_both}</strong> lipsesc ambele
                    </span>
                  )}
                </div>
                <Link
                  to="/sales?incomplete=true"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 mt-2"
                  data-testid="widget-incomplete-cta"
                >
                  Completează vânzările
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default IncompleteSalesWidget;
